<template>
  <div :class="['flex flex-col justify-between navContainer', hasProjectId ? 'p-0' : 'p-4']" aria-hidden>
    <div v-if="!hasProjectId" class="flex flex-col flex-1">
      <!-- Workspace -->
      <WorkspaceSelector :team-name="teamName" />

      <!-- Nav List -->
      <ul class="border-b border-app-gray-600 pb-5 mb-5">
        <li v-for="(item, index) in navItems" :key="index" :class="index !== navItems.length - 1 ? 'mb-1' : ''">
          <router-link
            v-if="item.title !== 'Verifiers' || legacyVerifierCount > 0"
            v-slot="{ navigate }"
            :to="{ name: item.name }"
            :aria-label="`link to ${item.title}`"
          >
            <a
              aria-hidden
              class="flex items-center justify-between text-base font-medium p-2 cursor-pointer hover:bg-app-gray-700 hover:text-app-white rounded-lg transition-colors"
              :class="navItemHighlighted(item.name) ? 'bg-app-gray-700 text-app-white' : 'text-app-gray-100'"
              @click="handleMenuClick(navigate)"
            >
              <div class="flex items-center">
                <Icon :name="item.icon" :class="navItemHighlighted(item.name) ? 'text-app-gray-900' : 'text-app-gray-400'"></Icon>
                <span class="ml-3 text-app-gray-900">{{ item.title }}</span>
                <Icon v-if="item.title === 'Billing' && hasOutstandingInvoices" name="exclamation-circle-solid-icon" class="text-app-error ml-1" />
              </div>
              <Badge v-if="item.title === 'Verifiers' && unlinkedLegacyVerifierCount > 0" variant="danger" class="w-max uppercase" pill>
                {{ unlinkedLegacyVerifierCount }}
              </Badge>
            </a>
          </router-link>
        </li>
      </ul>

      <!-- Project Quick Links -->
      <div class="pb-4 project-container">
        <h6 class="text-xs font-semibold text-app-gray-200 uppercase tracking-wider pl-3 mb-2">My Projects</h6>
        <ul id="project-quick-links" class="overflow-y-auto project-container-inner">
          <li v-for="p in projects" :id="p.project_id" :key="p.project_id" class="mt-1">
            <router-link
              v-slot="{ navigate }"
              :to="p.project_id === projectId ? '' : getProjectRoute(p.project_id)"
              :aria-label="`link to ${p.name}`"
            >
              <a
                aria-hidden
                class="flex items-center text-base font-medium p-2 cursor-pointer hover:bg-app-gray-700 rounded-lg transition-colors"
                :class="p.project_id === projectId ? 'bg-app-gray-700 text-app-white' : 'text-app-gray-100'"
                :title="p.name"
                @click="handleMenuClick(navigate)"
              >
                <ProjectAvatar :project="p" />
                <span class="ml-3 w-[80%] truncate">{{ p.name }}</span>
              </a>
            </router-link>
          </li>
        </ul>
        <div class="ml-2 mt-3">
          <AccessValidator :access="TEAM_PERMISSION.edit" :selected-team="selectedTeam">
            <Button variant="text" class="flex gap-2 items-center !text-app-gray-200 !p-0" @click="() => showCreateProject()">
              <div class="p-0 border border-app-gray-600 rounded-lg"><Icon name="plus-solid-icon" class="text-app-gray-200" /></div>
              <span>Add new project</span>
            </Button>
          </AccessValidator>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col flex-1">
      <div class="border-b border-app-gray-600 flex items-center px-4 py-4">
        <Button
          variant="text"
          class="flex gap-2 items-center !text-base !font-medium !text-app-gray-100 hover:!text-app-white hover:!no-underline !p-0 transition-colors"
          @click="() => $router.push(getRoute('projects'))"
        >
          <Icon name="arrow-left-icon" class="text-app-gray-200" />
          Back to all projects
        </Button>
      </div>
      <div class="flex flex-col gap-4 justify-center items-left p-4">
        <Select v-if="projectsLoaded" :model-value="projectId" :options="projectOptions" match-parents-width @update:model-value="handleProject" />
        <Select v-else />

        <div class="flex flex-col gap-4 h-[calc(100vh_-_360px)] overflow-y-auto">
          <div v-for="key in projectNavItems" :key="key.heading" class="flex flex-col gap-4">
            <p class="uppercase text-xs font-semibold text-app-gray-200 tracking-wider">{{ key.heading }}</p>
            <ul class="flex flex-col gap-0.5">
              <li v-for="(item, index) in key.items" :key="index" :class="index !== key.items.length - 1 ? 'mb-1' : ''">
                <router-link v-slot="{ navigate }" :to="getProjectDetailsRoute(projectId, item.to)" :aria-label="`link to ${item.title}`">
                  <div
                    aria-hidden
                    class="flex items-center justify-between text-base font-medium p-2 cursor-pointer hover:bg-app-gray-700 hover:text-app-white rounded-lg transition-colors"
                    :class="navItemHighlighted(item.name) ? 'bg-app-gray-700 text-app-white' : 'text-app-gray-100'"
                    @click="handleMenuClick(navigate)"
                  >
                    <div class="flex items-center">
                      <div class="relative">
                        <Icon :name="item.icon" :class="navItemHighlighted(item.name) ? 'text-app-gray-900' : 'text-app-gray-400'"></Icon>
                        <div v-if="showAlertCircle(item.title)" class="w-2 h-2 bg-app-red-500 rounded-full absolute right-[-2px] top-0" />
                      </div>
                      <span class="ml-3">{{ item.title }}</span>
                    </div>
                  </div>
                </router-link>
              </li>
            </ul>
          </div>

          <Card
            v-if="!sidebarDevnetNoticeHidden && isTestnetNetwork"
            :border="false"
            :classes="{ container: '!bg-app-gray-700 !border !border-app-gray-600' }"
          >
            <div class="pt-2 px-4 pb-4">
              <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center">
                  <Badge variant="warning">Devnet Environment</Badge>
                  <Button variant="text" class="!text-app-gray-200 !p-0" @click="sidebarDevnetNoticeHidden = 'true'">
                    <Icon name="x-solid-icon" class="text-app-gray-200" />
                  </Button>
                </div>
                <p class="leading-loose text-sm text-app-gray-100">
                  You are currently using Devnet environment, which is limited to 1,000 users. Switch to Mainnet for a more optimized experience.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>

    <!-- Bottom Nav List -->
    <ul class="border-app-gray-600" :class="['border-t', hasProjectId ? 'p-4' : 'pt-4']">
      <li>
        <a
          aria-hidden
          href="https://docs.metamask.io/embedded-wallets"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center text-base font-medium p-2 cursor-pointer hover:bg-app-gray-700 hover:text-app-white rounded-lg transition-colors text-app-gray-100"
        >
          <Icon name="document-text-solid-icon" class="text-app-gray-200"></Icon>
          <span class="ml-3">Docs</span>
        </a>
      </li>
      <li>
        <a
          aria-hidden
          href="https://status.web3auth.io"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center text-base font-medium p-2 cursor-pointer hover:bg-app-gray-700 hover:text-app-white rounded-lg transition-colors text-app-gray-100"
        >
          <Icon name="check-circle-solid-icon" class="text-app-gray-200"></Icon>
          <span class="ml-3">Status</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { Badge } from "@toruslabs/vue-components/Badge";
import { Icon } from "@toruslabs/vue-components/Icon";
import { useStorage } from "@vueuse/core";
import { defineComponent } from "vue";

import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import ProjectAvatar from "@/components/ProjectAvatar/ProjectAvatar.vue";
import { Select } from "@/components/Select";
import useGettingStartedHidden from "@/composables/useGettingStartedHidden";
import AccessValidator from "@/containers/Team/AccessValidator";
import { billingModule, projectModule, teamModule, userModule } from "@/store";
import verifierModule from "@/store/modules/verifier";
import { ADMIN, LOCAL_STORAGE, MAINNET_NETWORKS, ROUTES, TEAM_PERMISSION, TESTNET_NETWORKS } from "@/utils/enums";
import { Project } from "@/utils/interface";
import { hasPermission } from "@/utils/team";

import WorkspaceSelector from "../WorkspaceSelector";

export default defineComponent({
  name: "SideNavbar",
  components: { ProjectAvatar, Badge, Button, Icon, WorkspaceSelector, AccessValidator, Select, Card },
  props: {
    isMobileDrawer: {
      type: Boolean,
      default: false,
    },
    teamName: {
      type: String,
      required: true,
    },
    showCreateProject: {
      type: Function,
      required: true,
    },
  },
  setup() {
    return {
      gettingStartedHidden: useGettingStartedHidden(),
      TEAM_PERMISSION,
      TESTNET_NETWORKS,
      sidebarDevnetNoticeHidden: useStorage(LOCAL_STORAGE.sidebarDevnetNoticeHidden, null),
    };
  },
  data() {
    return {
      isCreateProject: false,
      bottomNavItems: [
        { title: "Docs", icon: "document-text-solid-icon", to: "https://docs.metamask.io/embedded-wallets" },
        { title: "Status", icon: "check-circle-solid-icon", to: "profile-settings" },
      ],
      showTeamOptions: false,
    };
  },
  computed: {
    selectedTeam() {
      return teamModule.selectedTeamGetter(this.teamName);
    },
    projects() {
      return projectModule.projectsGetter(this.selectedTeam.id);
    },
    projectsLoaded() {
      return this.projects.length > 0 || Boolean(this.project);
    },
    legacyVerifierCount() {
      return (verifierModule.legacyVerifiersGetter(this.selectedTeam.id) || []).length;
    },
    unlinkedLegacyVerifierCount() {
      return (verifierModule.legacyVerifiersGetter(this.selectedTeam.id) || []).filter((verifier) => !verifier.linked_projects).length;
    },
    projectId(): string {
      return (this.$route.params.projectId ?? "") as string;
    },
    project(): Project | undefined {
      return projectModule.projectGetter(this.selectedTeam.id, this.projectId);
    },
    hasBillingPermission() {
      if (!this.selectedTeam.id) return true;
      return hasPermission(this.selectedTeam.role ?? "", TEAM_PERMISSION.billing);
    },
    hasOutstandingInvoices() {
      return billingModule.hasPendingInvoices(this.selectedTeam.id);
    },
    isAdminUser() {
      return userModule.userProfileGetter?.role === ADMIN;
    },
    navItems(): { title: string; icon: string; to: string; name?: string }[] {
      return [
        ...(!this.gettingStartedHidden
          ? [
              {
                title: "Getting Started",
                icon: "light-bulb-solid-icon",
                to: "getting-started",
                name: ROUTES.GETTING_STARTED,
              },
            ]
          : []),
        {
          title: "Projects",
          icon: "document-report-solid-icon",
          to: "projects",
          name: ROUTES.TEAM_PROJECTS,
        },
        {
          title: "Verifiers",
          icon: "folder-remove-solid-icon",
          to: "verifiers",
          name: ROUTES.TEAM_VERIFIERS,
        },
        { title: "Archive", icon: "archive-solid-icon", to: "archive", name: ROUTES.TEAM_ARCHIVE },
        { title: "Settings", icon: "cog-solid-icon", to: "settings", name: ROUTES.TEAM_SETTINGS },
        ...(this.hasBillingPermission ? [{ title: "Billing", icon: "credit-card-solid-icon", to: "billing", name: ROUTES.TEAM_BILLING }] : []),
      ];
    },
    hasProjectId() {
      return this.$route.params.projectId !== undefined;
    },
    projectNavItems() {
      return [
        {
          heading: "General",
          items: [
            {
              title: "Project Settings",
              icon: "clipboard-list-solid-icon",
              to: "",
              name: ROUTES.TEAM_PROJECT_SETTINGS,
            },
            {
              title: "Analytics",
              icon: "document-report-solid-icon",
              to: "analytics",
              name: ROUTES.TEAM_PROJECT_ANALYTICS,
            },
          ],
        },
        {
          heading: "Authentication",
          items: [
            {
              title: "Chains & Networks",
              icon: "wifi-icon",
              to: "chain-networks",
              name: ROUTES.TEAM_PROJECT_CHAIN_NETWORKS,
            },
            {
              title: "Authentication",
              icon: "database-solid-icon",
              to: "authentication",
              name: ROUTES.TEAM_PROJECT_AUTHENTICATION,
            },
            {
              title: "Smart Accounts",
              icon: "document-text-solid-icon",
              to: "smart-accounts",
              name: ROUTES.TEAM_PROJECT_SMART_ACCOUNTS,
            },
          ],
        },
        {
          heading: "Wallet Services",
          items: [
            {
              title: "Wallet Configuration",
              icon: "wallet-solid-cog-icon",
              to: "wallet-configuration",
              name: ROUTES.TEAM_PROJECT_WALLET_CONFIGURATION,
            },
            {
              title: "Wallet Funding",
              icon: "cash-solid-icon",
              to: "wallet-funding",
              name: ROUTES.TEAM_PROJECT_WALLET_FUNDING,
            },
          ],
        },
        {
          heading: "Customization",
          items: [
            {
              title: "Branding",
              icon: "color-swatch-solid-icon",
              to: "branding",
              name: ROUTES.TEAM_PROJECT_BRANDING,
            },
            {
              title: "Login Modal",
              icon: "user-circle-solid-icon",
              to: "login-modal",
              name: ROUTES.TEAM_PROJECT_LOGIN_MODAL,
            },
          ],
        },
        {
          heading: "User Management",
          items: [
            {
              title: "Access Control",
              icon: "user-group-solid-icon",
              to: "access-control",
              name: ROUTES.TEAM_PROJECT_ACCESS_CONTROL,
            },
            {
              title: "Users",
              icon: "user-group-solid-icon",
              to: "users",
              name: ROUTES.TEAM_PROJECT_USERS,
            },
          ],
        },
      ];
    },
    projectOptions() {
      const active = this.projects;
      const current = this.project;
      const options = current && !active.some((project) => project.project_id === current.project_id) ? [...active, current] : active;
      return options.map((project) => ({
        name: `${project.name} (${project.network})`,
        value: project.project_id,
      }));
    },
    hasNotWhitelistedDomainsOnMainnet() {
      return MAINNET_NETWORKS.map((network) => network.id).includes(this.project.network) && this.project?.whitelists < 1;
    },
    isTestnetNetwork() {
      return TESTNET_NETWORKS.map((network) => network.id).includes(this.project?.network);
    },
  },
  methods: {
    handleMenuClick(navigate: () => void): void {
      if (this.isMobileDrawer) {
        userModule.closeMobileDrawer();
      }
      navigate();
    },
    getRoute(name: string) {
      return `/organization/${this.teamName}/${name}`;
    },
    getProjectRoute(clientId: string) {
      return `/organization/${this.teamName}/projects/${clientId}`;
    },
    getProjectDetailsRoute(clientId: string, to: string) {
      return to ? `/organization/${this.teamName}/projects/${clientId}/${to}` : `/organization/${this.teamName}/projects/${clientId}`;
    },
    showAlertCircle(item: string) {
      if (item === "Chains & Networks") {
        return !this.project?.chain_config;
      }
      if (item === "Project Settings") {
        return this.project && this.hasNotWhitelistedDomainsOnMainnet;
      }
      if (item === "Smart Accounts") {
        return projectModule.smartAccountsEIP4337EnabledButBundlerNotConfiguredForAll(this.selectedTeam.id, this.projectId);
      }
      return false;
    },
    handleProject(clientId: string) {
      const route = this.getProjectRoute(clientId);
      this.$router.push(route);
    },
    navItemHighlighted(itemName: string) {
      return this.$route.matched.find(({ name }) => name === itemName);
    },
  },
});
</script>

<style lang="scss" scoped>
@use "SideNavbar.scss";
</style>
